import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import { useSearchParams } from "react-router";

import { Icon, Message, Messages, Spinner } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Body } from "capitalrx-components-2.0/lib/typography";
import { PlanBenefit } from "capitalrx-types";

import { getPlan, listDocumentsForPlan } from "@/api/axios";
import { getFormulary } from "@/api/axios/formulary";
import { MemberDocument } from "@/api/types/Document";
import { useExternalLink, useNavigateSearch } from "@/premium/hooks";
import { useSelectedEnrollmentPlan } from "@/premium/hooks/enrollmentPlan";
import { Breadcrumbs, Card, NeedHelp, PageLayout, ThemedButton } from "@/premium/themedComponents";
import { ThemedH3 } from "@/premium/themedComponents/Headers";
import { ANALYTIC_EVENTS, logEvent } from "@/utils/analytics";

import { DocumentList } from "./DocumentList";
import { PlanDropdown } from "./PlanDropdown";

import "./Plan.scss";

const DOCUMENT_RESULTS_PER_PAGE = 50;

export const Plan: FC = () => {
  const { t: translate } = useTranslation();
  const {
    enrollmentPlanCsid,
    hideDrugPricing,
    hideMedicarePrescriptionPaymentPlanPage,
    hidePharmacySearch,
    hideFormularyLookup,
    overrideFormularyLookupURL,
    hidePlanDocuments,
  } = useSelectedEnrollmentPlan();

  const [formularyUrl, setFormularyUrl] = useState<string>(undefined);
  const [coverageStrategyType, setCoverageStrategyType] = useState<PlanBenefit["coverage_strategy_type"] | undefined>(undefined);
  const [planDocuments, setPlanDocuments] = useState<MemberDocument[]>([]);

  const [isLoadingPlan, setIsLoadingPlan] = useState<boolean>(true);
  const [isLoadingFormularyUrl, setIsLoadingFormularyUrl] = useState<boolean>(true);
  const [isLoadingPlanDocuments, setIsLoadingPlanDocuments] = useState<boolean>(true);

  const [searchParams] = useSearchParams();
  const enrollmentPlanId = searchParams.get("enrollmentPlan");
  const navigate = useNavigateSearch();
  const openExternalLink = useExternalLink();

  const medicarePartDStrategies = [
    "medicare_actuarially_equivalent_mapd",
    "medicare_basic_alternative_mapd",
    "medicare_defined_standard_mapd",
    "medicare_employer_group_waiver_plan_mapd",
    "medicare_enhanced_alternative_mapd",
    "medicare_actuarially_equivalent_pdp",
    "medicare_basic_alternative_pdp",
    "medicare_defined_standard_pdp",
    "medicare_employer_group_waiver_plan_pdp",
    "medicare_enhanced_alternative_pdp",
    "medicare_prescription_payment_plan",
  ];

  useEffect(() => {
    if (enrollmentPlanCsid) {
      setIsLoadingPlan(true);
      getPlan(enrollmentPlanCsid.id)
        .then(({ plan_benefit }) => {
          setCoverageStrategyType(plan_benefit?.coverage_strategy_type);
        })
        .catch(() => {
          setCoverageStrategyType(undefined);
        })
        .finally(() => {
          setIsLoadingPlan(false);
        });

      setIsLoadingFormularyUrl(true);
      getFormulary(enrollmentPlanCsid.id)
        .then((formulary) => {
          setFormularyUrl(formulary.lookup_tool_url);
        })
        .catch(() => {
          setFormularyUrl(undefined);
        })
        .finally(() => {
          setIsLoadingFormularyUrl(false);
        });
    } else {
      setCoverageStrategyType(undefined);
      setIsLoadingPlan(false);

      setFormularyUrl(undefined);
      setIsLoadingFormularyUrl(false);
    }

    if (enrollmentPlanCsid && !hidePlanDocuments) {
      setIsLoadingPlanDocuments(true);
      listDocumentsForPlan(enrollmentPlanCsid.id, DOCUMENT_RESULTS_PER_PAGE)
        .then((results) => {
          setPlanDocuments(results.results.map((r) => r.document));
        })
        .catch(() => {
          setPlanDocuments([]);
        })
        .finally(() => {
          setIsLoadingPlanDocuments(false);
        });
    } else {
      setIsLoadingPlanDocuments(false);
      setPlanDocuments([]);
    }
  }, [enrollmentPlanCsid, hidePlanDocuments]);

  const sendToFormulary = useCallback(() => {
    logEvent(ANALYTIC_EVENTS.VIEW_FORMULARY);
    const url = overrideFormularyLookupURL ?? formularyUrl;
    if (!url) return;
    openExternalLink(url);
  }, [formularyUrl, overrideFormularyLookupURL, openExternalLink]);

  const sendToPharmacies = useCallback(() => {
    logEvent(ANALYTIC_EVENTS.PHARMACY_AND_DRUG_SEARCH);
    navigate("/plan/find-pharmacies", {
      enrollmentPlan: enrollmentPlanId,
    });
  }, [enrollmentPlanId, navigate]);

  const sendToM3P = useCallback(() => {
    logEvent(ANALYTIC_EVENTS.M3P);
    navigate("/plan/medicare-prescription-payment-plan", {
      enrollmentPlan: enrollmentPlanId,
    });
  }, [enrollmentPlanId, navigate]);

  const isMedicarePartDCSID = useMemo(() => {
    return medicarePartDStrategies.includes(coverageStrategyType);
  }, [coverageStrategyType]);

  const isLoadingMedicareTile = isLoadingPlan;
  const isLoadingBottomOfPage = isLoadingPlanDocuments || isLoadingMedicareTile;

  useEffect(() => {
    logEvent(ANALYTIC_EVENTS.PLAN_BENEFIT);
  }, []);

  return (
    <PageLayout>
      <Stack spacing="large">
        <Stack>
          <Breadcrumbs />
          <PlanDropdown />
        </Stack>
        <Stack>
          {!hidePharmacySearch && (
            <Card>
              <Stack className="plan-full-height-stack">
                <ThemedH3>
                  {translate("PLAN.plan.findPharmacies")} {!hideDrugPricing && `${translate("PLAN.plan.andDrugPricing")}`}{" "}
                  {translate("PLAN.plan.forThisPlan")}
                </ThemedH3>
                <p className="plan-themed-p">
                  {translate("PLAN.plan.searchToSeeInNetworkPharmacies")}{" "}
                  {!hideDrugPricing && `${translate("PLAN.plan.comparePricingForSpecific")}`}.
                </p>
                <Stack.Spacer />
                <Shelf>
                  <ThemedButton onClick={sendToPharmacies}>{translate("PLAN.plan.start")}</ThemedButton>
                  <Shelf.Spacer />
                </Shelf>
                <Stack.Spacer />
              </Stack>
            </Card>
          )}

          {!hideFormularyLookup && (
            <Card>
              <Stack className="plan-full-height-stack">
                <ThemedH3>{translate("PLAN.plan.seeWhatDrugsAreCovered")}</ThemedH3>
                <p className="plan-themed-p">{translate("PLAN.plan.viewOurFormulary")}</p>
                <Shelf>
                  {isLoadingFormularyUrl ? (
                    <Skeleton width={215} height={36} />
                  ) : (
                    <ThemedButton onClick={sendToFormulary}>
                      <Shelf>
                        <>
                          <Icon name="link-out" />
                          {translate("PLAN.plan.viewFormulary")}
                        </>
                      </Shelf>
                    </ThemedButton>
                  )}
                  <Shelf.Spacer />
                </Shelf>
                <Stack.Spacer />
                <Message appearance={Messages.Warning} visible>
                  <Body style={{ color: "#313E48" }}>{translate("PLAN.plan.someDrugsMayNotBeCoveredByThisPlan")}</Body>
                </Message>
              </Stack>
            </Card>
          )}

          {isLoadingBottomOfPage && (
            <Card>
              <Stack>
                <Shelf>
                  <Shelf.Spacer />
                  <ThemedH3>{translate("PLAN.plan.loading")}</ThemedH3>
                  <Shelf.Spacer />
                </Shelf>
                <Shelf>
                  <Shelf.Spacer />
                  <Spinner />
                  <Shelf.Spacer />
                </Shelf>
              </Stack>
            </Card>
          )}

          {!isLoadingBottomOfPage && !hideMedicarePrescriptionPaymentPlanPage && isMedicarePartDCSID && (
            <Card>
              <Stack className="plan-full-height-stack">
                <ThemedH3>{translate("PLAN.plan.medicarePrescriptionPaymentPlan")}</ThemedH3>
                <p className="plan-themed-p">{translate("PLAN.plan.needAdditionalMedicare")}</p>
                <Stack.Spacer />
                <Shelf>
                  <ThemedButton onClick={sendToM3P}>{translate("PLAN.plan.MoreProgramInformation")}</ThemedButton>
                  <Shelf.Spacer />
                </Shelf>
              </Stack>
            </Card>
          )}

          {!isLoadingBottomOfPage && !hidePlanDocuments && enrollmentPlanCsid && (
            <DocumentList documents={planDocuments} enrollmentPlanId={enrollmentPlanCsid.id} />
          )}
        </Stack>
        <NeedHelp />
      </Stack>
    </PageLayout>
  );
};
